import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import {
  saveOfflineList,
  getOfflineList,
  getAllOfflineLists,
  deleteOfflineList,
  batchSaveOfflinePlaces,
  getOfflinePlace,
  getOfflineStats,
  clearAllOfflineData,
  isListOffline,
  setOfflineMeta,
  getOfflineMeta,
} from '../utils/indexedDB';
import api from '../services/api';

/**
 * OfflineContext
 *
 * Provides offline data access and download functionality for premium users.
 * Features:
 * - Track online/offline status
 * - Download lists for offline access
 * - Pre-cache photos via service worker
 * - Manage offline storage
 */

const OfflineContext = createContext();

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

// Photo cache name must match service worker
const PHOTO_CACHE_NAME = 'vibary-photos-v1';

export function OfflineProvider({ children }) {
  const { user } = useAuth();
  const { isPremium } = useSubscription();

  // Online/offline state
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Offline data state
  const [offlineListIds, setOfflineListIds] = useState(new Set());
  const [downloadingListIds, setDownloadingListIds] = useState(new Set());
  const [downloadProgress, setDownloadProgress] = useState({}); // listId -> { current, total, phase }

  // Stats
  const [stats, setStats] = useState(null);

  // Track user ID for cache invalidation
  const lastUserIdRef = useRef(null);

  // Cancellation tracking
  const cancelledDownloadsRef = useRef(new Set());

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load offline list IDs on mount and user change
  useEffect(() => {
    const loadOfflineListIds = async () => {
      if (!user) {
        // Clear offline data reference when logged out
        if (lastUserIdRef.current) {
          setOfflineListIds(new Set());
          lastUserIdRef.current = null;
        }
        return;
      }

      const userId = user.userId || user.id;

      // If user changed, clear old data
      if (lastUserIdRef.current && lastUserIdRef.current !== userId) {
        await clearAllOfflineData();
        setOfflineListIds(new Set());
      }
      lastUserIdRef.current = userId;

      // Load offline lists for current user
      const offlineLists = await getAllOfflineLists(userId);
      setOfflineListIds(new Set(offlineLists.map((l) => l.id)));

      // Load stats
      const newStats = await getOfflineStats();
      setStats(newStats);
    };

    loadOfflineListIds();
  }, [user]);

  /**
   * Check if a list is downloaded for offline use
   */
  const isListDownloaded = useCallback(
    (listId) => {
      return offlineListIds.has(listId);
    },
    [offlineListIds]
  );

  /**
   * Check if a list is currently being downloaded
   */
  const isListDownloading = useCallback(
    (listId) => {
      return downloadingListIds.has(listId);
    },
    [downloadingListIds]
  );

  /**
   * Get download progress for a list
   */
  const getDownloadProgress = useCallback(
    (listId) => {
      return downloadProgress[listId] || null;
    },
    [downloadProgress]
  );

  /**
   * Pre-cache photos using the service worker
   * Since photos are stored in Firebase Storage, we just need to warm the HTTP cache
   * @returns {boolean} true if completed, false if cancelled
   */
  const preCachePhotos = async (listId, photoUrls, onProgress) => {
    if (!photoUrls || photoUrls.length === 0) return true;

    // Filter to only Firebase Storage URLs
    const firebaseUrls = photoUrls.filter(
      (url) =>
        url &&
        (url.includes('firebasestorage.googleapis.com') ||
          (url.includes('storage.googleapis.com') && url.includes('Signature=')))
    );

    if (firebaseUrls.length === 0) return true;

    // Use Cache API directly (faster than service worker messages)
    try {
      const cache = await caches.open(PHOTO_CACHE_NAME);
      let completed = 0;

      // Fetch in parallel with concurrency limit
      const concurrencyLimit = 5;
      for (let i = 0; i < firebaseUrls.length; i += concurrencyLimit) {
        // Check for cancellation before each batch
        if (cancelledDownloadsRef.current.has(listId)) {
          return false;
        }

        const batch = firebaseUrls.slice(i, i + concurrencyLimit);
        await Promise.all(
          batch.map(async (url) => {
            try {
              // Check if already cached
              const cached = await cache.match(url);
              if (!cached) {
                const response = await fetch(url, { mode: 'cors' });
                if (response.ok) {
                  await cache.put(url, response);
                }
              }
              completed++;
              onProgress?.(completed, firebaseUrls.length);
            } catch {
              // Silently fail individual photos
              completed++;
              onProgress?.(completed, firebaseUrls.length);
            }
          })
        );
      }
      return true;
    } catch (error) {
      console.warn('Failed to pre-cache photos:', error);
      return true; // Still consider it complete even if caching failed
    }
  };

  /**
   * Download a list for offline access
   * Premium-only feature
   * @returns {{ success: boolean, cancelled?: boolean, placeCount?: number, photoCount?: number }}
   */
  const downloadList = useCallback(
    async (listId, options = {}) => {
      if (!isPremium) {
        throw new Error('Offline downloads require premium');
      }

      if (downloadingListIds.has(listId)) {
        return { success: false }; // Already downloading
      }

      // Clear any previous cancellation for this list
      cancelledDownloadsRef.current.delete(listId);

      // Mark as downloading
      setDownloadingListIds((prev) => new Set([...prev, listId]));
      setDownloadProgress((prev) => ({ ...prev, [listId]: { current: 0, total: 0, phase: 'fetching' } }));

      try {
        // Check for early cancellation
        if (cancelledDownloadsRef.current.has(listId)) {
          return { success: false, cancelled: true };
        }

        // 1. Fetch full list data from API (with full place data)
        const response = await api.getList(listId, { includePlaceData: 'full' });
        const list = response.data;

        if (!list) {
          throw new Error('List not found');
        }

        // Check for cancellation after fetch
        if (cancelledDownloadsRef.current.has(listId)) {
          return { success: false, cancelled: true };
        }

        // Add userId for indexing
        list.userId = user?.userId || user?.id;

        // 2. Save list to IndexedDB
        await saveOfflineList(list);

        // 3. Extract and save place data
        const places = (list.items || [])
          .filter((item) => item.placeId)
          .map((item) => ({
            placeId: item.placeId,
            name: item.name,
            address: item.address,
            types: item.types,
            location: item.location,
            geometry: item.geometry,
            openingHours: item.openingHours,
            website: item.website,
            phoneNumber: item.phoneNumber,
            rating: item.rating,
            priceLevel: item.priceLevel,
            googleMapsUrl: item.googleMapsUrl || item.url,
            storedPhotoUrl: item.storedPhotoUrl || item.photoUrl,
          }));

        if (places.length > 0) {
          await batchSaveOfflinePlaces(places);
        }

        // 4. Pre-cache photos (Phase 5)
        const photoUrls = places
          .map((p) => p.storedPhotoUrl)
          .filter(Boolean);

        if (photoUrls.length > 0) {
          setDownloadProgress((prev) => ({
            ...prev,
            [listId]: { current: 0, total: photoUrls.length, phase: 'photos' },
          }));

          const photosCompleted = await preCachePhotos(listId, photoUrls, (current, total) => {
            setDownloadProgress((prev) => ({
              ...prev,
              [listId]: { current, total, phase: 'photos' },
            }));
          });

          // If photo caching was cancelled, clean up and return
          if (!photosCompleted) {
            await deleteOfflineList(listId);
            return { success: false, cancelled: true };
          }
        }

        // 5. Update state
        setOfflineListIds((prev) => new Set([...prev, listId]));

        // 6. Update stats
        const newStats = await getOfflineStats();
        setStats(newStats);

        // 7. Save download metadata
        await setOfflineMeta(`download_${listId}`, {
          downloadedAt: Date.now(),
          placeCount: places.length,
          photoCount: photoUrls.length,
        });

        return { success: true, placeCount: places.length, photoCount: photoUrls.length };
      } catch (error) {
        // If cancelled, don't throw
        if (cancelledDownloadsRef.current.has(listId)) {
          return { success: false, cancelled: true };
        }
        console.error('Failed to download list:', error);
        throw error;
      } finally {
        // Clean up cancellation tracking
        cancelledDownloadsRef.current.delete(listId);

        setDownloadingListIds((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
        setDownloadProgress((prev) => {
          const next = { ...prev };
          delete next[listId];
          return next;
        });
      }
    },
    [isPremium, downloadingListIds, user]
  );

  /**
   * Cancel a download in progress
   */
  const cancelDownload = useCallback((listId) => {
    cancelledDownloadsRef.current.add(listId);
  }, []);

  /**
   * Remove a list from offline storage
   */
  const removeOfflineList = useCallback(async (listId) => {
    await deleteOfflineList(listId);
    setOfflineListIds((prev) => {
      const next = new Set(prev);
      next.delete(listId);
      return next;
    });

    // Update stats
    const newStats = await getOfflineStats();
    setStats(newStats);
  }, []);

  /**
   * Get a list from offline storage (falls back to API if online)
   */
  const getList = useCallback(
    async (listId, options = {}) => {
      // Try offline first if available
      const offlineList = await getOfflineList(listId);

      if (!isOnline) {
        // Offline - return cached data or null
        return offlineList;
      }

      // Online - fetch fresh data
      if (!options.offlineOnly) {
        try {
          const response = await api.getList(listId, options);
          return response.data;
        } catch (error) {
          // If API fails and we have offline data, use it
          if (offlineList) {
            return offlineList;
          }
          throw error;
        }
      }

      return offlineList;
    },
    [isOnline]
  );

  /**
   * Get a place from offline storage
   */
  const getPlace = useCallback(
    async (placeId) => {
      return getOfflinePlace(placeId);
    },
    []
  );

  /**
   * Clear all offline data
   */
  const clearOfflineData = useCallback(async () => {
    await clearAllOfflineData();
    setOfflineListIds(new Set());
    setStats({
      photoCount: 0,
      statusCount: 0,
      offlineListCount: 0,
      offlinePlaceCount: 0,
    });

    // Also clear photo cache
    try {
      await caches.delete(PHOTO_CACHE_NAME);
    } catch {
      // Ignore cache deletion errors
    }
  }, []);

  /**
   * Refresh stats
   */
  const refreshStats = useCallback(async () => {
    const newStats = await getOfflineStats();
    setStats(newStats);
    return newStats;
  }, []);

  /**
   * Estimate storage size (rough approximation)
   */
  const getStorageEstimate = useCallback(async () => {
    if (navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          usage: estimate.usage || 0,
          quota: estimate.quota || 0,
          usagePercent: estimate.quota ? (estimate.usage / estimate.quota) * 100 : 0,
        };
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  const value = {
    // State
    isOnline,
    isPremium,
    offlineListIds: Array.from(offlineListIds),
    stats,

    // Download management
    downloadList,
    cancelDownload,
    removeOfflineList,
    isListDownloaded,
    isListDownloading,
    getDownloadProgress,

    // Data access
    getList,
    getPlace,

    // Storage management
    clearOfflineData,
    refreshStats,
    getStorageEstimate,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

export default OfflineContext;
